import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportCountOfUserSignupsData,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, CHART_COLORS } from '@hooks/useReportChartData';
import { toast } from 'react-toastify';
import {
  exportCountOfSignupsUsers,
  getCountOfSignupsUsers,
} from '@src/services/reports';
import { buildCommonPayloadWithGroupingAndProducts } from '@src/utils/buildCommonPayload';
import { downloadFile } from '@src/lib/utils';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
    name: string;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.name}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              {entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const CountOfUserSignupsIncludePending: FC = () => {
  // Data state
  const { auth } = useAuthStore();
  const [data, setData] = useState<TReportCountOfUserSignupsData[]>([]);
  const [summary, setSummary] = useState<
    | {
        count: number;
        count_previous: number;
        count_previous_year: number;
      }
    | undefined
  >(undefined);

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
  });

  // Use custom hook for table columns generation
  const { columns, displayItems, tableData } = useReportTableColumns({
    data,
    selectedItems: ['count'],
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item) => item.count || 0,
    getPreviousPeriodField: (item) => item.count_previous || 0,
    getPreviousYearField: (item) => item.count_previous_year || 0,
    columnTitleFormatter: (key) => {
      if (key === 'count') return 'COUNT OF SIGNUPS';
      if (key === 'count_previous') return 'COUNT OF SIGNUPS (Previous Period)';
      if (key === 'count_previous_year')
        return 'COUNT OF SIGNUPS (Previous Year)';

      return key;
    },
    includeTotalsRow: true,
    summaryRow: summary,
  });

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item) => item.count || 0,
    getPreviousPeriodField: (item) => item.count_previous || 0,
    getPreviousYearField: (item) => item.count_previous_year || 0,
  });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildCommonPayloadWithGroupingAndProducts(filters);
      const response = await getCountOfSignupsUsers(payload);

      setData(response.data);
      setSummary(response.summary);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to generate report');
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const payload = buildCommonPayloadWithGroupingAndProducts(filters);
      const response = await exportCountOfSignupsUsers(payload);

      downloadFile(response.data, 'count-of-user-signups-include-pending');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Count of User Signups Include Pending
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              />

              {/* Main data lines */}
              {displayItems.map((item, index) => (
                <Line
                  key={item}
                  activeDot={{ r: 6 }}
                  dataKey={item}
                  dot={{
                    fill: CHART_COLORS[index % CHART_COLORS.length],
                    r: 4,
                  }}
                  name="Count of Signups"
                  stroke={CHART_COLORS[index % CHART_COLORS.length]}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}

              {/* Previous period lines */}
              {filters.compareToPreviousPeriod &&
                displayItems.map((item, index) => (
                  <Line
                    key={`${item}_previous_period`}
                    activeDot={{ r: 4 }}
                    dataKey={`${item}_previous_period`}
                    dot={{
                      fill: CHART_COLORS[index % CHART_COLORS.length],
                      r: 3,
                    }}
                    name="Count of Signups (Previous Period)"
                    stroke={CHART_COLORS[index % CHART_COLORS.length]}
                    strokeDasharray="5 5"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}

              {/* Previous year lines */}
              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((item, index) => (
                  <Line
                    key={`${item}_previous_year`}
                    activeDot={{ r: 4 }}
                    dataKey={`${item}_previous_year`}
                    dot={{
                      fill: CHART_COLORS[index % CHART_COLORS.length],
                      r: 3,
                    }}
                    name="Count of Signups (Previous Year)"
                    stroke={CHART_COLORS[index % CHART_COLORS.length]}
                    strokeDasharray="2 2"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={handleExportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
